import { getData } from './api-wrapper.service';
import { UserSearchResult, getProfileByUid } from './profile.service';
import { searchService } from '../search.service';
import logger from '../../utils/logger';

// Constants for limiting expensive operations
const MAX_FRIENDS_TO_CHECK = 5; // Only check first 5 friends for friend-of-friend suggestions
const MAX_FOF_PER_FRIEND = 10; // Only check first 10 friend-of-friends per friend

/**
 * Map PostgreSQL user search result to frontend UserSearchResult format
 */
const mapToUserSearchResult = (pgUser: any): UserSearchResult => ({
  uid: pgUser.uid,
  username: pgUser.username || '',
  displayName: pgUser.display_name || `${pgUser.first_name || ''} ${pgUser.last_name || ''}`.trim() || pgUser.username || '',
  profilePhotoURL: pgUser.photo_url || undefined,
  isInstructor: pgUser.is_instructor || false
});

/**
 * Get suggested friends based on:
 * 1. Friends of friends (via Firebase - allowed per-user reads)
 * 2. Users from PostgreSQL search (instructors, active users)
 *
 * Optimized for performance:
 * - Limits depth of friend-of-friend traversal
 * - Parallel profile fetching
 * - Uses PostgreSQL for bulk user queries (no Firebase permission issues)
 */
export const getSuggestedFriends = async (
  uid: string,
  limit: number = 10
): Promise<UserSearchResult[]> => {
  try {
    logger.info(`[FriendSuggestions] Starting for user ${uid}, limit: ${limit}`);
    const startTime = Date.now();

    // Get the current user's friends
    const userFriends = await getData(`/users/${uid}/friends`);
    const userFriendIds = userFriends ? Object.keys(userFriends) : [];

    // Store existing friends and already processed users to avoid duplicates
    const processedUserIds = new Set<string>();
    processedUserIds.add(uid);
    userFriendIds.forEach(id => processedUserIds.add(id));

    // Initialize suggested friends array
    const suggestedFriends: UserSearchResult[] = [];

    // 1. Friends of friends (most relevant suggestions) - PARALLELIZED
    // This works because we're reading specific user paths, not querying /users
    if (userFriendIds.length > 0) {
      const friendsToCheck = userFriendIds.slice(0, MAX_FRIENDS_TO_CHECK);
      logger.info(`[FriendSuggestions] Checking ${friendsToCheck.length} friends for friend-of-friend suggestions`);

      // Fetch all friend's friend lists in parallel
      const friendFriendsPromises = friendsToCheck.map(friendId =>
        getData(`/users/${friendId}/friends`).catch(() => null)
      );
      const friendFriendsResults = await Promise.all(friendFriendsPromises);

      // Collect unique friend-of-friend IDs
      const fofIdsToFetch: string[] = [];
      for (const friendFriends of friendFriendsResults) {
        if (friendFriends) {
          const friendOfFriendIds = Object.keys(friendFriends).slice(0, MAX_FOF_PER_FRIEND);
          for (const fofId of friendOfFriendIds) {
            if (!processedUserIds.has(fofId) && !fofIdsToFetch.includes(fofId)) {
              fofIdsToFetch.push(fofId);
              processedUserIds.add(fofId);
            }
          }
        }
      }

      logger.info(`[FriendSuggestions] Found ${fofIdsToFetch.length} unique friend-of-friends to fetch`);

      // Fetch profiles in parallel batches
      if (fofIdsToFetch.length > 0) {
        const profilesToFetch = fofIdsToFetch.slice(0, limit * 2);
        const profilePromises = profilesToFetch.map(fofId =>
          getProfileByUid(fofId, { skipAdminCheck: true })
            .then(profile => ({ fofId, profile }))
            .catch(() => ({ fofId, profile: null }))
        );
        const profileResults = await Promise.all(profilePromises);

        for (const { fofId, profile } of profileResults) {
          if (profile && suggestedFriends.length < limit) {
            suggestedFriends.push({
              uid: fofId,
              username: profile.username,
              displayName: profile.displayName || `${profile.firstName} ${profile.lastName}`,
              profilePhotoURL: profile.profilePhotoURL || profile.photoURL,
              isInstructor: profile.isInstructor
            });
          }
        }
      }
    }

    logger.info(`[FriendSuggestions] After FOF phase: ${suggestedFriends.length} suggestions (${Date.now() - startTime}ms)`);

    // Return early if we have enough suggestions
    if (suggestedFriends.length >= limit) {
      logger.info(`[FriendSuggestions] Complete with ${suggestedFriends.length} suggestions in ${Date.now() - startTime}ms`);
      return suggestedFriends.slice(0, limit);
    }

    // 2. Get instructors and active users via PostgreSQL search
    // This avoids Firebase permission issues by using the PostgreSQL API
    const remaining = limit - suggestedFriends.length;

    try {
      // Search for instructors using PostgreSQL
      const instructorResults = await searchService.searchUsers('instructor', remaining * 2);

      for (const pgUser of instructorResults) {
        if (suggestedFriends.length >= limit) break;
        if (processedUserIds.has(pgUser.uid)) continue;
        if (!pgUser.is_instructor) continue;

        processedUserIds.add(pgUser.uid);
        suggestedFriends.push(mapToUserSearchResult(pgUser));
      }

      logger.info(`[FriendSuggestions] After instructors: ${suggestedFriends.length} suggestions`);
    } catch (error) {
      logger.debug('[FriendSuggestions] Could not fetch instructors from PostgreSQL:', error);
    }

    // 3. Get more users via a broad search if still needed
    if (suggestedFriends.length < limit) {
      try {
        // Search for any users - PostgreSQL will return most relevant
        const moreUsers = await searchService.searchUsers('martial arts', (limit - suggestedFriends.length) * 2);

        for (const pgUser of moreUsers) {
          if (suggestedFriends.length >= limit) break;
          if (processedUserIds.has(pgUser.uid)) continue;

          processedUserIds.add(pgUser.uid);
          suggestedFriends.push(mapToUserSearchResult(pgUser));
        }

        logger.info(`[FriendSuggestions] After broad search: ${suggestedFriends.length} suggestions`);
      } catch (error) {
        logger.debug('[FriendSuggestions] Could not fetch additional users from PostgreSQL:', error);
      }
    }

    logger.info(`[FriendSuggestions] Complete with ${suggestedFriends.length} suggestions in ${Date.now() - startTime}ms`);
    return suggestedFriends;
  } catch (error) {
    logger.error('Error getting suggested friends:', error);
    throw error;
  }
};